import React, { useState, useEffect, useCallback } from 'react';
import { tarihteMetni, noListesi, satisOzeti, elenenleriGrupla } from '../lib/satis';
import YayineviSecici from './YayineviSecici';

// ============================================================================
// SATIŞA EKLE modalı (2026-09-13) — ortak bileşen.
// Hem ana soru listesinden (App.js, toplu seçim çubuğu) hem Satış Arşivi'nden
// (SoruSatisi: numarayla seç + fiyatlandırma) aynı şekilde açılır: seçili
// public_no listesine TEK yayınevi + tarih + birim fiyat uygulanır. authFetch
// imzası AuthContext.authFetch(url, options) ile birebir aynı olmalı.
// Basım ekranı bu modalı KULLANMAZ (2026-09-15): yayınevine teslim PDF indirme
// anında sunucuda yazılır (ayarlar.yayineviTeslim + yayineviId + birimFiyat).
//
// 2026-09-15 — yayınevi DB listesi (kullanıcı kararı): serbest metin yerine
//   `YayineviSecici` (zorunlu; listede yoksa "+ Yeni" ile eklenir). Gövdeye
//   `yayinevi_id` gider; eski `yayinevi` metin alanı ve öneri (datalist) ucu
//   KALKTI. Varsayılan seçim `varsayilanYayineviId/Ad` ile gelir (Satış
//   Arşivi fiyatlandırma: seçili teslimlerin yayınevi).
//
// 2026-09-14 — münhasır satış sözleşmesi:
//   * POST /api/satis HEP-YA-HİÇ: 409 `satis_reddedildi` gelirse HİÇBİR şey
//     yazılmamıştır. Modal KAPANMAZ; engeller dört grup hâlinde listelenir
//     (zaten satılmış · uygun değil · bulunamadı · bağlam grubu eksik).
//   * `grup_eksik` doluysa "Kardeşleri de ekle ve kaydet" aynı isteği
//     `grup_tamamla:true` ile yineler — yalnız kayıtsız kardeş (`eksik`) varken ve
//     hiçbir grubun kardeşi başka yayınevinde (`engel`, 2026-09-15) değilken. Kardeşi
//     başka yayınevinde olan soru "Engelli N soruyu listeden çıkar" ile çıkarılır
//     (bağlam temelli sorular ayrı satılamaz — kullanıcı kararı 2026-09-15).
//   * Başarıda kısa özet gösterilir; "Tamam" (ya da ✕ / arka plan) `onTamam(d)`
//     çağırır — çağıranların imzası değişmedi.
//   * `mod='fiyatlandir'`: Satış Arşivi'nin "Fiyat bekleyen teslimler" sekmesi.
//     Aynı uç — backend fiyatsız PDF teslimini fiyatlandırır (`fiyatlanan`).
//   * `elenenler` + `seciliSayisi` (2026-09-14, kullanıcı: "13 soru seçili ama 1 soru
//     için fiyat hesaplıyor"): ana listedeki toplu seçimden satışa UYGUN OLMAYANLAR
//     (onaysız · satılmış · çapa · 1 puan · bulunamadı) modalın İÇİNDE nedenleriyle
//     listelenir — geçici bildirimle kaybolmaz.
// ============================================================================
const bugun = () => new Date().toISOString().slice(0, 10);

// `uygun_degil[].neden` sunucu metni ya da kısa kod olabilir — bilinen kodlar
// Türkçeleştirilir, bilinmeyen değer okunur hâle getirilip aynen basılır.
const NEDEN_ETIKET = {
  onayli_degil: 'onaylı değil',
  onaylanmamis: 'onaylı değil',
  onay_bekliyor: 'onay bekliyor (onaylı değil)',
  reddedildi: 'reddedilmiş',
  durum_uygun_degil: 'durumu satışa uygun değil',
};
const nedenMetni = (n) => NEDEN_ETIKET[n] || String(n || 'satışa uygun değil').replace(/_/g, ' ');

function RedGrubu({ baslik, children, ton = 'red' }) {
  const renk = ton === 'amber' ? 'text-amber-900' : 'text-red-900';
  return (
    <div>
      <div className={`font-bold ${renk}`}>{baslik}</div>
      <ul className={`mt-0.5 ml-4 list-disc space-y-0.5 ${renk}`}>{children}</ul>
    </div>
  );
}

export default function SatisEkleModal({
  apiUrl, authFetch, publicNolar = [], onKapat, onTamam,
  mod = 'satis', varsayilanYayineviId = null, varsayilanYayineviAd = '', elenenler = [], seciliSayisi = null,
  qcUyusmazlik = [],
}) {
  // İç liste: "Engelli soruları çıkar" yalnız bu kopyayı daraltır, çağıranın
  // dizisine dokunmaz. İçerik değişirse (yeni seçim) yeniden kurulur.
  const [nolar, setNolar] = useState(() => [...publicNolar]);
  const nolarAnahtar = publicNolar.join(',');
  useEffect(() => { setNolar(nolarAnahtar ? nolarAnahtar.split(',').map(Number) : []); }, [nolarAnahtar]);

  // Yayınevi: {id, ad} | null — yalnız DB listesinden (YayineviSecici).
  const [yayinevi, setYayinevi] = useState(() => (varsayilanYayineviId != null
    ? { id: Number(varsayilanYayineviId), ad: varsayilanYayineviAd || '' }
    : null));
  const [tarih, setTarih] = useState(bugun());
  const [birimFiyat, setBirimFiyat] = useState('');
  const [notMetni, setNotMetni] = useState('');
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [red, setRed] = useState(null);       // 409 satis_reddedildi gövdesi
  const [sonuc, setSonuc] = useState(null);   // başarılı yanıt → özet ekranı

  const fiyatSayi = Number(String(birimFiyat).replace(',', '.'));
  const fiyatGecerli = String(birimFiyat).trim() !== '' && Number.isFinite(fiyatSayi) && fiyatSayi >= 0;
  const toplam = fiyatGecerli ? fiyatSayi * nolar.length : null;

  const gonder = useCallback(async ({ grupTamamla = false } = {}) => {
    if (nolar.length === 0) { setHata('Kaydedilecek soru kalmadı'); return; }
    if (!yayinevi || yayinevi.id == null) { setHata('Yayınevi seçin'); return; }
    if (!fiyatGecerli) { setHata('Birim fiyat sıfır veya pozitif bir sayı olmalı'); return; }
    setGonderiliyor(true);
    setHata(null);
    try {
      const govde = {
        public_no: nolar,
        yayinevi_id: yayinevi.id,
        satis_tarihi: tarih || undefined,
        birim_fiyat: fiyatSayi,
        not_metni: notMetni.trim() || undefined,
      };
      if (grupTamamla) govde.grup_tamamla = true;
      const r = await authFetch(`${apiUrl}/api/satis`, { method: 'POST', body: JSON.stringify(govde) });
      const d = await r.json().catch(() => ({}));
      if (r.status === 409 || (d && d.red_nedeni === 'satis_reddedildi')) {
        // Hep-ya-hiç reddi: hiçbir şey yazılmadı → modal açık, engeller listelenir.
        setRed(d || {});
        setGonderiliyor(false);
        return;
      }
      if (!d || d.basarili !== true) {
        setRed(null);
        setHata((d && d.hata) || `Satış kaydedilemedi${r.status ? ` (HTTP ${r.status})` : ''}`);
        setGonderiliyor(false);
        return;
      }
      setRed(null);
      setSonuc(d);
      setGonderiliyor(false);
    } catch (err) {
      setHata('Satış kaydedilemedi: ' + err.message);
      setGonderiliyor(false);
    }
  }, [apiUrl, authFetch, yayinevi, tarih, fiyatSayi, fiyatGecerli, notMetni, nolar]);

  const kaydet = (e) => { e.preventDefault(); gonder(); };

  // Başarıdan sonra her kapanış yolu (Tamam / ✕ / arka plan) onTamam'a gider —
  // çağıran listesini tazelesin; aksi hâlde yazılmış satış ekranda görünmezdi.
  const kapat = () => {
    if (sonuc) { if (onTamam) onTamam(sonuc); return; }
    if (onKapat) onKapat();
  };

  const zatenSatilmis = (red && Array.isArray(red.zaten_satilmis)) ? red.zaten_satilmis : [];
  const uygunDegil = (red && Array.isArray(red.uygun_degil)) ? red.uygun_degil : [];
  const bulunamayan = (red && Array.isArray(red.bulunamayan)) ? red.bulunamayan : [];
  const grupEksik = (red && Array.isArray(red.grup_eksik)) ? red.grup_eksik : [];
  // Bağlam grubu (2026-09-15, kullanıcı kararı "bağlam temelli sorular ayrı ayrı
  // satılamayacak"): `eksik` = kayıtsız ve seçimde olmayan kardeş (eklenince giderilir);
  // `engel` = başka yayınevinde kaydı olan kardeş (eklemek ÇÖZMEZ — grup ayrı satılamaz).
  const dizi = (x) => (Array.isArray(x) ? x : []);
  const grupEngelli = grupEksik.filter((g) => dizi(g.engel).length > 0);
  // "Kardeşleri de ekle" yalnız eklenecek kardeş varken ve hiçbir grup başka yayınevine
  // bağlı değilken anlamlıdır.
  const kardesEklenebilir = grupEksik.some((g) => dizi(g.eksik).length > 0) && grupEngelli.length === 0;
  // Seçimden çıkarılınca giderilebilen engeller (kayıtsız kardeş eksiği kardeş eklenerek
  // giderilir; kardeşi başka yayınevinde olan soru ancak çıkarılarak).
  const engelliNolar = [...new Set([
    ...zatenSatilmis.map((z) => Number(z.public_no)),
    ...uygunDegil.map((u) => Number(u.public_no)),
    ...bulunamayan.map(Number),
    ...grupEngelli.map((g) => Number(g.public_no)),
  ].filter(Number.isFinite))];
  const engellileriCikar = () => {
    const cikar = new Set(engelliNolar);
    setNolar((p) => p.filter((n) => !cikar.has(Number(n))));
    setRed(null);
    setHata(null);
  };

  const fiyatlandirma = mod === 'fiyatlandir';
  const baslik = fiyatlandirma
    ? `💰 ${nolar.length} teslim için fiyat girişi`
    : (nolar.length === 0 && elenenler.length > 0
      ? '💰 Satışa uygun soru yok'
      : `💰 ${nolar.length} soru için satış kaydı`);
  const seciliToplam = seciliSayisi ?? (publicNolar.length + elenenler.length);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={kapat}>
      {sonuc ? (
        <div onClick={(e) => e.stopPropagation()} data-satis-sonuc
          className="bg-white rounded-xl shadow-xl w-full max-w-md p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-bold text-sm text-emerald-800">✓ {satisOzeti(sonuc)}</div>
            <button type="button" onClick={kapat} className="text-gray-400 hover:text-gray-700" aria-label="Kapat">✕</button>
          </div>
          <ul className="text-[12px] text-gray-700 space-y-1">
            <li>
              <span className="font-semibold">Eklenen:</span>{' '}
              {(sonuc.eklenen || []).length} soru
              {(sonuc.eklenen || []).length > 0 && <span className="text-gray-500"> ({noListesi(sonuc.eklenen)})</span>}
            </li>
            {(sonuc.fiyatlanan || []).length > 0 && (
              <li>
                <span className="font-semibold">Fiyatlandırılan teslim:</span> {sonuc.fiyatlanan.length}
                <span className="text-gray-500"> ({noListesi(sonuc.fiyatlanan)})</span>
              </li>
            )}
            {(sonuc.grup_eklenen || []).length > 0 && (
              <li>
                <span className="font-semibold">Bunlardan bağlam kardeşi:</span> {sonuc.grup_eklenen.length}
                <span className="text-gray-500"> ({noListesi(sonuc.grup_eklenen)})</span>
              </li>
            )}
          </ul>
          <div className="flex justify-end pt-1">
            <button type="button" onClick={kapat} autoFocus
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700">
              Tamam
            </button>
          </div>
        </div>
      ) : (
      <form onClick={(e) => e.stopPropagation()} onSubmit={kaydet} data-satis-modal
        className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-bold text-sm text-emerald-800">{baslik}</div>
          <button type="button" onClick={kapat} className="text-gray-400 hover:text-gray-700" aria-label="Kapat">✕</button>
        </div>
        {fiyatlandirma && (
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
            PDF ile teslim edilmiş, fiyatı girilmemiş kayıtlar bu fiyatla satışa dönüşür.
          </div>
        )}

        {/* Cevap QC (2026-09-15, KAPI C): kör denetim ya da öğretmenin kör onayı anahtarla
            uyuşmayan sorular UYARILIR — satış engellenmez (engelleme kullanıcı kararı). */}
        {qcUyusmazlik.length > 0 && (
          <div data-satis-qc
            className="text-[11px] text-red-900 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5 space-y-0.5">
            <div className="font-bold">⚠ Cevap QC uyuşmazlığı ({qcUyusmazlik.length} soru): {noListesi(qcUyusmazlik, 15)}</div>
            <div>Kör denetim bu soruların anahtarını doğrulayamadı; satmadan önce panelde "Cevap QC: ⚠ Uyuşmazlık" süzgeciyle kontrol edin.</div>
          </div>
        )}

        {/* Toplu seçimden elenenler: elenen soru SESSİZCE düşmez, nedeniyle burada durur. */}
        {elenenler.length > 0 && (
          <div data-satis-elenen
            className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 space-y-1">
            <div className="font-bold">
              {publicNolar.length === 0
                ? `Seçili ${seciliToplam} sorudan hiçbiri satışa uygun değil:`
                : `Seçili ${seciliToplam} sorudan ${publicNolar.length} tanesi satışa alınabilir; ${elenenler.length} soru bu satışa girmedi:`}
            </div>
            <ul className="ml-4 list-disc space-y-0.5">
              {elenenleriGrupla(elenenler).map((g) => (
                <li key={g.neden}>
                  {g.neden} ({g.sayi}){g.nolar.length > 0 ? `: ${noListesi(g.nolar, 15)}` : ''}
                </li>
              ))}
            </ul>
            <div className="text-amber-800">Satış yalnız onaylı, satılmamış ve hatalı işaretlenmemiş sorularla yapılır.</div>
          </div>
        )}

        {/* Yayınevi yalnız DB listesinden; listede yoksa "+ Yeni" ekler ve seçer. */}
        <YayineviSecici apiUrl={apiUrl} authFetch={authFetch}
          value={yayinevi ? yayinevi.id : null} valueAd={yayinevi ? yayinevi.ad : ''}
          onChange={(y) => { setYayinevi(y); setHata(null); }}
          zorunlu etiket="Yayınevi / Firma" bosSecenek="Yayınevi seçin…" autoFocus className="w-full" />

        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">Tarih</label>
            <input type="date" value={tarih} onChange={(e) => setTarih(e.target.value)}
              className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 text-sm" />
          </div>
          <div className="flex-1">
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">Birim fiyat (₺)</label>
            <input type="text" inputMode="decimal" value={birimFiyat}
              onChange={(e) => setBirimFiyat(e.target.value)} placeholder="0.00"
              className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 text-sm" />
          </div>
        </div>

        {fiyatGecerli && (
          <div className="text-[11px] text-emerald-700 font-semibold">
            {nolar.length} × {fiyatSayi.toLocaleString('tr-TR')} ₺ = {toplam.toLocaleString('tr-TR')} ₺
          </div>
        )}

        <div>
          <label className="block text-[11px] font-semibold text-gray-600 mb-1">Not (isteğe bağlı)</label>
          <textarea value={notMetni} onChange={(e) => setNotMetni(e.target.value)} rows={2} maxLength={2000}
            className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 text-sm resize-none" />
        </div>

        {/* 409 — hep-ya-hiç reddi: hiçbir kayıt yazılmadı; engeller gruplu. */}
        {red && (
          <div data-satis-red className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-[11px] space-y-2">
            <div className="font-bold text-red-800">
              ⛔ {red.hata || 'Satış reddedildi'} — hiçbir kayıt yazılmadı.
            </div>
            {zatenSatilmis.length > 0 && (
              <RedGrubu baslik={`Zaten satılmış (${zatenSatilmis.length})`}>
                {zatenSatilmis.map((z) => {
                  const kime = z.yayinevi ? `${z.yayinevi} yayınevine` : 'başka bir yayınevine';
                  const ne = tarihteMetni(z.satis_tarihi);
                  return (
                    <li key={`z-${z.public_no}`}>
                      #{z.public_no} — {kime}{ne ? ` ${ne}` : ''}{' '}
                      {z.fiyatli === false ? 'teslim edildi (fiyat bekliyor)' : 'satıldı'}
                    </li>
                  );
                })}
              </RedGrubu>
            )}
            {uygunDegil.length > 0 && (
              <RedGrubu baslik={`Satışa uygun değil (${uygunDegil.length})`}>
                {uygunDegil.map((u) => (
                  <li key={`u-${u.public_no}`}>#{u.public_no} — {nedenMetni(u.neden)}</li>
                ))}
              </RedGrubu>
            )}
            {bulunamayan.length > 0 && (
              <RedGrubu baslik={`Bulunamadı (${bulunamayan.length})`}>
                <li>{noListesi(bulunamayan, 40)}</li>
              </RedGrubu>
            )}
            {grupEksik.length > 0 && (
              <RedGrubu ton={grupEngelli.length > 0 ? 'red' : 'amber'}
                baslik={`${grupEngelli.length > 0 ? 'Bağlam grubu ayrı satılamaz' : 'Bağlam grubu eksik'} (${grupEksik.length})`}>
                {grupEksik.map((g) => {
                  const eksik = dizi(g.eksik);
                  const engel = dizi(g.engel);
                  return (
                    <li key={`g-${g.public_no}`}>
                      #{g.public_no} — bağlam grubu{g.baglam_grubu ? ` “${g.baglam_grubu}”` : ''}:
                      {eksik.length > 0 && ` kardeş ${noListesi(eksik)} seçimde yok (grup birlikte satılır)`}
                      {engel.map((e, i) => (
                        <span key={`ge-${e.public_no}`} data-grup-engel-kardes={e.public_no}>
                          {eksik.length > 0 || i > 0 ? ';' : ''} kardeş #{e.public_no} başka yayınevinde
                          {e.yayinevi ? ` (${e.yayinevi})` : ''} — grup ayrı satılamaz
                        </span>
                      ))}
                    </li>
                  );
                })}
              </RedGrubu>
            )}
            <div className="flex flex-wrap gap-2 pt-0.5">
              {kardesEklenebilir && (
                <button type="button" onClick={() => gonder({ grupTamamla: true })} disabled={gonderiliyor}
                  className="px-2.5 py-1 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-40">
                  🔗 Kardeşleri de ekle ve kaydet
                </button>
              )}
              {engelliNolar.length > 0 && (
                <button type="button" onClick={engellileriCikar} disabled={gonderiliyor}
                  title="Zaten satılmış / uygun olmayan / bulunamayan ya da bağlam kardeşi başka yayınevinde olan soruları bu satıştan çıkarır; ardından yeniden kaydedin"
                  className="px-2.5 py-1 rounded-lg border border-red-300 bg-white text-red-700 font-semibold hover:bg-red-100 disabled:opacity-40">
                  Engelli {engelliNolar.length} soruyu listeden çıkar
                </button>
              )}
            </div>
            {kardesEklenebilir && engelliNolar.length > 0 && (
              <div className="text-red-700">Kardeş eklense de diğer engeller kalkmadıkça satış yazılmaz.</div>
            )}
            {grupEngelli.length > 0 && (
              <div data-grup-engel-not className="text-red-700">
                Bağlam temelli sorular ayrı satılamaz: kardeşi başka yayınevinde olan soruyu bu satıştan
                çıkarın — kardeş eklemek bunu çözmez.
              </div>
            )}
          </div>
        )}

        {hata && <div className="text-[11px] text-red-600 font-semibold">{hata}</div>}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={kapat}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 hover:bg-gray-50">
            Vazgeç
          </button>
          <button type="submit" disabled={gonderiliyor || nolar.length === 0}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">
            {gonderiliyor ? 'Kaydediliyor…' : (fiyatlandirma ? 'Fiyatı Kaydet' : 'Satışı Kaydet')}
          </button>
        </div>
      </form>
      )}
    </div>
  );
}
